// Type definitions for API endpoint configuration
export type ApiType = "auth" | "customer" | "system" | "without-iehub-customer";

// Simplified API endpoint builder
const buildEndpoint = (path: string, apiType: ApiType = "system"): string => {
  if (!path) {
    throw new Error("Path is required for endpoint building");
  }

  const cleanPath = path.startsWith("/") ? path.slice(1) : path;

  // Return full paths for all API types (baseURL is now empty)
  if (apiType === "customer") {
    return `/iehub-customer/api/v1/${cleanPath}`;
  } else if (apiType === "without-iehub-customer") {
    return `/api/v1/${cleanPath}`;
  } else {
    // Auth and system APIs both use /api prefix
    return `/api/${cleanPath}`;
  }
};

// Authentication endpoints (use 'auth' API type)
export const authEndpoints = {
  login: buildEndpoint("auth/login", "auth"),
  logout: buildEndpoint("auth/logout", "auth"),
  refresh: buildEndpoint("auth/refresh-token", "auth"),
  validateToken: buildEndpoint("auth/validate-token", "auth"),
  register: buildEndpoint("auth/register", "auth"),
  profile: buildEndpoint("auth/profile", "auth"),
  changePassword: buildEndpoint("auth/change-password", "auth"),
  forgotPassword: buildEndpoint("auth/forgot-password", "auth"),
  resetPassword: buildEndpoint("auth/reset-password", "auth"),
  signup: buildEndpoint("auth/signup", "auth"),
  verifyUser: buildEndpoint("auth/verify-user", "auth"),
  magicLink: buildEndpoint("auth/magic-link", "auth"),
  createUser: buildEndpoint("auth/create-user", "auth"),
  signin: buildEndpoint("auth/signin", "auth"),
};

// OAuth endpoints (use 'auth' API type)
export const oauthEndpoints = {
  googleAuthUrl: buildEndpoint("oauth/google/auth-url", "auth"),
  googleExchange: buildEndpoint("oauth/google/exchange", "auth"),
};

// Meta conversions (server-side Meta CAPI forwarder)
export const metaEndpoints = {
  metaConversions: buildEndpoint("auth/conversions/meta", "auth"),
};

// User management endpoints (use 'system' API type - default)
export const userEndpoints = {
  list: buildEndpoint("users"),
  get: (id: string | number) => buildEndpoint(`users/${id}`),
  create: buildEndpoint("users"),
  update: (id: string | number) => buildEndpoint(`users/${id}`),
  delete: (id: string | number) => buildEndpoint(`users/${id}`),
  search: buildEndpoint("users/search"),
  avatar: (id: string | number) => buildEndpoint(`users/${id}/avatar`),
};

// Customer management endpoints (use 'customer' API type)
export const customerEndpoints = {
  list: buildEndpoint("ie_free_post_customers", "customer"),
  get: (id: string | number) =>
    buildEndpoint(`ie_free_post_customers/${id}`, "customer"),
  create: buildEndpoint("ie_free_post_customers", "customer"),
  update: buildEndpoint("ie_free_post_customers", "customer"),
  delete: (id: string | number) =>
    buildEndpoint(`ie_free_post_customers/${id}`, "customer"),
  search: buildEndpoint("ie_free_post_customers/search", "customer"),
  listLenders: buildEndpoint("ie-mstr-lender-api", "customer"),
  /**
   * Energy companies endpoint - always requires NI postcode parameter
   * @param isNorthernIreland - true for NI postcodes (sends Y), false for non-NI (sends N)
   */
  listEnergyCompanies: (isNorthernIreland: boolean) =>
    buildEndpoint(
      `ie-energy-companies-mstr?hasNorthernIrelandSupplierRelatedPostcode=${isNorthernIreland ? "Y" : "N"}`,
      "customer",
    ),
  listBroadbandSuppliers: buildEndpoint(
    "ie-broad-band-supplier-mstr",
    "customer",
  ),
  listLandlineSuppliers: buildEndpoint("ie-landline-supplier-mstr", "customer"),
  listMobileSuppliers: buildEndpoint("ie-mobile-supplier-mstr", "customer"),
  listWaterCompanies: buildEndpoint("ie-water-companies-mstr", "customer"),
  // Digital lenders list – use customer API namespace for consistency:
  // GET /iehub-customer/api/v1/ie-mstr-lender-digital-list
  listDigitalLenders: buildEndpoint("ie-mstr-lender-digital-list", "customer"),
  listFreepostLenders: buildEndpoint(
    "ie-mstr-lender-freepost-list",
    "customer",
  ),
  listNotSharedLenders: (customerId: string | number) =>
    buildEndpoint(
      `ie-mstr-lender-supporter-not-shared/${customerId}`,
      "customer",
    ),

  // Profile endpoints
  getCustomerProfile: (customerId: string | number) =>
    buildEndpoint(`customer-profile/${customerId}`, "customer"), // GET
  patchCustomerProfile: (customerId: string | number) =>
    buildEndpoint(`customer-profile/${customerId}`, "customer"), // PATCH
  patchCustomerData: (customerId: string | number) =>
    buildEndpoint(`customers/${customerId}`, "customer"), // PATCH
  getCustomerData: (userId: string | number) =>
    buildEndpoint(`customers/${userId}`, "customer"), // GET
  getProfileLookups: () =>
    buildEndpoint(
      "lookups?types=accommodationType,employmentStatus,tenure",
      "customer",
    ), // GET
  logButtonAction: () => buildEndpoint("ie-event-log", "customer"), // POST
  getTotalEnergyBills: (customerId: string | number) =>
    buildEndpoint(`ie-total-energy-bills-v/${customerId}`, "customer"), // GET
  getCalculatedTotals: (customerId: string | number) =>
    buildEndpoint(`ie-calculated-totals/${customerId}`, "customer"), // GET
  // Customer presented/answered summary
  getCustomerPresentedAnswered: (customerId: string | number) =>
    buildEndpoint(`customer-presented-answered/${customerId}`, "customer"), // GET
  showAdditionalSupportCompanies: () =>
    buildEndpoint(`show-additional-support-companies`, "customer"), // GET
  // Marketing opt-out endpoints
  getMarketingOptOut: (customerId: string | number) =>
    buildEndpoint(`ie-customer-marketing-opt-out/${customerId}`, "customer"), // GET
  createMarketingOptOut: () =>
    buildEndpoint("ie-customer-marketing-opt-out", "customer"), // POST
  updateMarketingOptOut: () =>
    buildEndpoint("ie-customer-marketing-opt-out", "customer"), // PUT
  patchMarketingOptOut: (id: string | number) =>
    buildEndpoint(`ie-customer-marketing-opt-out/${id}`, "customer"), // PATCH
  // Account deletion request (legacy parity with IeDeleteAccountEmail/insert) donot call this one
  requestAccountDeletion: () => buildEndpoint("delete-account", "customer"), // POST
  // New Delete Request controller (preferred when available)
  createDeleteRequest: () =>
    buildEndpoint("ie-customer-delete-request", "customer"), // POST
  // Support endpoints
  contactUs: () => buildEndpoint("contact-us", "customer"), // POST
  getCustomerSharedDocs: (
    customerId: string | number,
    companyName: string,
    referenceNumber: string,
  ) =>
    buildEndpoint(
      `customer-shared-doc-preview/${customerId}/${companyName}/${referenceNumber}`,
      "customer",
    ), // GET
  notifyCustomerDebtAdvice: () =>
    buildEndpoint("Ie-notify-customer-debt-advice", "customer"), // POST
  customerPrefrences: () => buildEndpoint("customer-preferences", "customer"), // POST
  updateCustomerPrefrences: (id: string) =>
    buildEndpoint(`customer-preferences/${id}`, "customer"), // PUT
  sendEmailOnShare: () => buildEndpoint("send-email-on-share", "customer"), // POST
  getCustomerPreferences: (customerId: string) => buildEndpoint(`customer-preferences/customer/${customerId}`, "customer"), // GET
};

// Terms and conditions endpoints (use 'customer' API type)
export const termsEndpoints = {
  acceptTerms: buildEndpoint("terms-and-conditions-accepted", "customer"), // POST
};

// Form/Screen endpoints
export const formEndpoints = {
  list: buildEndpoint("forms"),
  get: (id: string | number) => buildEndpoint(`forms/${id}`),
  submit: (id: string | number) => buildEndpoint(`forms/${id}/submit`),
  validate: (id: string | number) => buildEndpoint(`forms/${id}/validate`),
  schema: (id: string | number) => buildEndpoint(`forms/${id}/schema`),
  preview: (id: string | number) => buildEndpoint(`forms/${id}/preview`),
};

// File upload endpoints
export const fileEndpoints = {
  upload: buildEndpoint("files/upload"),
  get: (id: string | number) => buildEndpoint(`files/${id}`),
  delete: (id: string | number) => buildEndpoint(`files/${id}`),
  thumbnail: (id: string | number) => buildEndpoint(`files/${id}/thumbnail`),
};

// Application/System endpoints
export const systemEndpoints = {
  health: buildEndpoint("system/health"),
  version: buildEndpoint("system/version"),
  config: buildEndpoint("system/config"),
  features: buildEndpoint("system/features"),
};

// Analytics/Monitoring endpoints (FEMA integration)
export const monitoringEndpoints = {
  errors: buildEndpoint("monitoring/errors"),
  metrics: buildEndpoint("monitoring/metrics"),
  events: buildEndpoint("monitoring/events"),
  performance: buildEndpoint("monitoring/performance"),
};

// Notification endpoints
export const notificationEndpoints = {
  list: buildEndpoint("notifications"),
  markRead: (id: string | number) => buildEndpoint(`notifications/${id}/read`),
  markAllRead: buildEndpoint("notifications/read-all"),
  subscribe: buildEndpoint("notifications/subscribe"),
  unsubscribe: buildEndpoint("notifications/unsubscribe"),
};

// Onboarding endpoints (use 'customer' API type with clean paths)
export const onboardingEndpoints = {
  updateCustomer: buildEndpoint("customers", "customer"), // PUT
  patchCustomer: (id: string | number) =>
    buildEndpoint(`customers/${id}`, "customer"), // GET
  listCustomers: buildEndpoint("customers", "customer"), // GET
  getCustomerByUserId: (userId: string | number) =>
    buildEndpoint(`customers/${userId}`, "customer"), // GET
  getCustomerVerificationDetails: (userId: string | number) =>
    buildEndpoint(`isCustomerVerificationRequired/${userId}`, "customer"), // GET
  // Creditor portal: Open Banking invitations
  // GET /iehub-customer/api/v1/ie-openbank-invitation/{userId}
  getOpenBankInvitationsByUserId: (userId: string | number) =>
    buildEndpoint(`ie-openbank-invitation/${userId}`, "customer"), // GET
  // PUT /iehub-customer/api/v1/ie-openbank-invitation
  updateOpenBankInvitation: buildEndpoint("ie-openbank-invitation", "customer"), // PUT
  updateCustomerVerification: buildEndpoint(
    `customer-verification`,
    "customer",
  ), // POST
  createCustomer: buildEndpoint("ie_free_post_customers", "customer"), // POST
  listLenders: buildEndpoint("ie-mstr-lender-api", "customer"), // GET
  accountTypesByCompany: (companyName: string) =>
    buildEndpoint(
      `account-types-by-company/${encodeURIComponent(companyName)}`,
      "customer",
    ), // GET
  allAccountTypes: buildEndpoint("account-type-codes", "customer"), // GET

  // vulnerability circumstances apis
  getVulnerabilityCircumstances: (customerId: number) =>
    buildEndpoint(`ie-vulnerable-circumstances/${customerId}`, "customer"), // GET
  createVulnerabilityCircumstances: buildEndpoint(
    "ie-vulnerable-circumstances",
    "customer",
  ), // POST
  updateVulnerabilityCircumstances: (recordId: number) =>
    buildEndpoint(`ie-vulnerable-circumstances/${recordId}`, "customer"), // PUT

  // debts apis
  getDebts: (customerId: number) =>
    buildEndpoint(`debts/${customerId}`, "customer"), // GET
  updateDebts: buildEndpoint(`debts`, "customer"), // PUT
  createDebts: buildEndpoint(`debts`, "customer"), // POST
  deleteDebts: (customerId: number, debtId: number) =>
    buildEndpoint(`debts/${customerId}/${debtId}`, "customer"), // DELETE

  // vulnerability
  getVulnerability: (customerId: number) =>
    buildEndpoint(`ie-vulnerable-circumstances/${customerId}`, "customer"), // GET
  createVulnerability: buildEndpoint("ie-vulnerable-circumstances", "customer"), // POST
  updateVulnerability: buildEndpoint(`ie-vulnerable-circumstances`, "customer"), // PUT

  // customer field stage
  getCustomerFieldStage: (customerId: number) =>
    buildEndpoint(`customer-field-stage/${customerId}`, "customer"), // GET
  createCustomerFieldStage: buildEndpoint("customer-field-stage", "customer"), // POST
  updateCustomerFieldStage: buildEndpoint("customer-field-stage", "customer"), // PUT

  getPostcodesByPostCode: (
    postcodeTrimmed: string,
    customerId?: string | number | null,
  ) => {
    const query =
      customerId !== null && customerId !== undefined && `${customerId}` !== ""
        ? `?customerId=${encodeURIComponent(String(customerId))}`
        : "";

    return postcodeTrimmed
      ? buildEndpoint(
          `ie-post_code_list_vs_client_by-pcode/${encodeURIComponent(postcodeTrimmed)}${query}`,
          "customer",
        )
      : buildEndpoint(`ie-post_code_list_vs_client_by-pcode${query}`, "customer");
  },

  getPostcodesByCountry: (postcode: string | number) =>
    postcode
      ? buildEndpoint(`ie-postcodes-by-country/${postcode}`, "customer")
      : buildEndpoint("ie-postcodes-by-country", "customer"), // GET

  getPostcodeRegion: (postcodeDistrict: string | number) =>
    postcodeDistrict
      ? buildEndpoint(`ie-postcode-region/${postcodeDistrict}`, "customer")
      : buildEndpoint("ie-postcode-region", "customer"), // GET
};

// Budget/Financial endpoints (use 'customer' API type)
export const budgetEndpoints = {
  // Income data endpoints
  getIncomeData: (customerId: string | number) =>
    buildEndpoint(`income/${customerId}`, "customer"), // GET
  updateIncomeData: () => buildEndpoint("income", "customer"), // PUT
  getIncomeNotes: (customerId: string | number) =>
    buildEndpoint(`income-notes/${customerId}`, "customer"), // GET
  updateIncomeNotes: () => buildEndpoint("income-notes", "customer"), // PUT

  // Asset data endpoints
  getAssetData: (customerId: string | number) =>
    buildEndpoint(`income/${customerId}`, "customer"), // GET
  updateAssetData: () => buildEndpoint("income", "customer"), // PUT
  getAssetNotes: (customerId: string | number) =>
    buildEndpoint(`income-notes/${customerId}`, "customer"), // GET
  updateAssetNotes: () => buildEndpoint("income-notes", "customer"), // PUT

  // expense data endpoints
  getExpenseData: (customerId: string | number) =>
    buildEndpoint(`expenses/${customerId}`, "customer"), // GET
  updateExpenseData: () => buildEndpoint("expenses", "customer"), // PUT
  getExpenseNotes: (customerId: string | number) =>
    buildEndpoint(`expenses-notes/${customerId}`, "customer"), // GET
  updateExpenseNotes: () => buildEndpoint("expenses-notes", "customer"), // PUT

  // Guidance and triggers endpoints
  getGuidanceNotes: () => buildEndpoint("ie-guidance-notes", "customer"), // GET
  getIncomeTriggers: () => buildEndpoint("ie-income-triggers", "customer"), // GET
  getExpenseTriggers: () =>
    buildEndpoint("ie-expenses-triggers-api", "customer"), // GET

  // Analytics endpoints
  logButtonAction: () => buildEndpoint("ie-event-log", "customer"), // POST
};

export const debtEndpoints = {
  list: (customerId: string | number) =>
    buildEndpoint(`debts/${customerId}`, "customer"),
  priorityList: (customerId: string | number) =>
    buildEndpoint(`priority-debts/${customerId}`, "customer"),
  nonPriorityList: (customerId: string | number) =>
    buildEndpoint(`non-priority-debts/${customerId}`, "customer"),
  create: () => buildEndpoint("debts", "customer"),
  update: () => buildEndpoint("debts", "customer"),
  updateById: (debtId: string | number) =>
    buildEndpoint(`debts/${debtId}`, "customer"),
  delete: (customerId: string | number, debtId: string | number) =>
    buildEndpoint(`debts/${customerId}/${debtId}`, "customer"),
  paymentOffer: {
    get: (
      customerId: string | number,
      lenderName: string,
      accountNumber: string,
    ) =>
      buildEndpoint(
        `ie-customer-payment-offer/customer/${customerId}/lender/${encodeURIComponent(lenderName)}/account/${encodeURIComponent(accountNumber)}`,
        "customer",
      ),
    create: () => buildEndpoint("ie-customer-payment-offer", "customer"),
    update: (paymentOfferId: string | number) =>
      buildEndpoint(`ie-customer-payment-offer/${paymentOfferId}`, "customer"),
    delete: (paymentOfferId: string | number) =>
      buildEndpoint(`ie-customer-payment-offer/${paymentOfferId}`, "customer"),
  },
  freePostSharing: {
    list: (customerId: string | number) =>
      buildEndpoint(`ie-free-post-customer-sharing/${customerId}`, "customer"),
    create: () => buildEndpoint("ie-free-post-customer-sharing", "customer"),
    update: () => buildEndpoint("ie-free-post-customer-sharing", "customer"),
    updateById: (id: string | number) =>
      buildEndpoint(`ie-free-post-customer-sharing/${id}`, "customer"),
  },
  customerSharing: {
    list: (customerId: string | number) =>
      buildEndpoint(`ie-customer-sharing/${customerId}`, "customer"),
    create: () => buildEndpoint("ie-customer-sharing", "customer"),
    update: () => buildEndpoint("ie-customer-sharing", "customer"),
    updateById: (id: string | number) =>
      buildEndpoint(`ie-customer-sharing/${id}`, "customer"),
    latestSnapshot: () =>
      buildEndpoint("ie-cust-sharing-latest-snapshot", "customer"),
  },
};

// Help & Support endpoints (use 'customer' API type)
export const supportEndpoints = {
  // Filled section summary (legacy parity)
  getFilledSectionSummaryV: (customerId: string | number) =>
    buildEndpoint(`ie-filled-section-summary-v/${customerId}`, "customer"), // GET

  // External link logs
  logExternalLink: () => buildEndpoint("external-link-logs", "customer"), // POST
};

// Benefits / InBest endpoints (customer API)
export const benefitsEndpoints = {
  // Modern InBest calculate endpoint
  calculateInbest: () => buildEndpoint("inbest/calculate", "customer"), // POST → /iehub-customer/api/v1/inbest/calculate
};

// Report/Export endpoints (legacy parity for budget PDF + email)
// These map to legacy system endpoints under /api/* with modern auth headers
export const reportEndpoints = {
  generateBudgetPdf: (customerId: string | number) =>
    buildEndpoint(`generate-report/${customerId}`, "customer"), // GET
  deleteBudgetPdf: () => buildEndpoint("ie-customer-print/delete", "customer"), // POST
  emailBudget: (customerId: string | number) =>
    buildEndpoint(`ie-customer-email/${customerId}`, "customer"), // GET
};

// Legacy endpoints (external link logging)
export const legacyEndpoints = {
  logExternalLink: () => buildEndpoint("IeExternalLinkLog/insert", "system"), // POST → /api/IeExternalLinkLog/insert
};

// Utility function to get all endpoints for debugging
export const getAllEndpoints = () => ({
  auth: authEndpoints,
  oauth: oauthEndpoints,
  user: userEndpoints,
  customer: customerEndpoints,
  terms: termsEndpoints,
  form: formEndpoints,
  file: fileEndpoints,
  system: systemEndpoints,
  monitoring: monitoringEndpoints,
  notification: notificationEndpoints,
  onboarding: onboardingEndpoints,
  budget: budgetEndpoints,
  support: supportEndpoints,
  benefits: benefitsEndpoints,
  legacy: legacyEndpoints,
  dependents: dependentsEndpoints,
  uuEvidence: uuEvidenceEndpoints,
});

export const sharePageEndpoints = {
  getCustomerSharing: (customerId: string | number) =>
    buildEndpoint(`ie-customer-sharing/${customerId}`, "customer"), // GET
  getFreePostCustomerSharing: (customerId: string | number) =>
    buildEndpoint(`ie-free-post-customer-sharing/${customerId}`, "customer"), // GET
  /**
   * DELETE a freepost sharing row by its record id, e.g. /ie-free-post-customer-sharing/103100.
   */
  deleteFreePostCustomerSharing: (freePostSharingRecordId: string | number) =>
    buildEndpoint(
      `ie-free-post-customer-sharing/${freePostSharingRecordId}`,
      "customer",
    ),
  createCustomerSharing: () => buildEndpoint(`ie-customer-sharing`, "customer"), // POST
  createFreepostSharing: () =>
    buildEndpoint(`ie-free-post-customer-sharing`, "customer"), // POST
  getCustomerLatestSnapshot: (customerId: string | number) =>
    buildEndpoint(`ie-cust-sharing-latest-snapshot/${customerId}`, "customer"), // GET
  createCustomerLatestSnapshot: () =>
    buildEndpoint(`ie-cust-sharing-latest-snapshot`, "customer"), // POST
  insertCustomerSharingDetails: () =>
    buildEndpoint(`ie-update-customer-sharing-details`, "customer"), // POST
  createCadentAdditionalQuestions: () =>
    buildEndpoint(`ie-cadent-additional-questions`, "customer"), // POST
  getCadentAdditionalQuestions: (customerId: string | number) =>
    buildEndpoint(`ie-cadent-additional-questions/${customerId}`, "customer"), // GET
};

export const documentsPageEndpoints = {
  getCustomerDocuments: (customerId: string | number) =>
    buildEndpoint(`customer-documents/${customerId}`, "customer"), // GET
  downloadCustomerDocument: (fileId: string | number) =>
    buildEndpoint(`customer-documents/download/${fileId}`, "customer"), // GET
  uploadCustomerDocument: (
    customerId: string | number,
    isDocUploadedFromDecl: string,
    entrySource: string,
  ) =>
    buildEndpoint(
      `customer-documents/upload?customerId=${customerId}&isDocUploadedFromDecl=${encodeURIComponent(
        isDocUploadedFromDecl ?? "",
      )}&entrySource=${encodeURIComponent(entrySource ?? "")}`,
      "customer",
    ), // POST
  deleteCustomerDocument: (customerId: string | number, id: string | number) =>
    buildEndpoint(`customer-documents/${customerId}/${id}`, "customer"), // DELETE
  updateCustomerDocument: () => buildEndpoint(`customer-documents`, "customer"), // PUT
  updateSharedDocuments: (id: string | number) =>
    buildEndpoint(`customer-shared-documents/${id}`, "customer"), // PUT
};

export const smartReviewEndpoints = {
  getSmartReview: (customerId: string | number) =>
    buildEndpoint(`ie-smart-review/${customerId}`, "customer"), // GET
  createSmartReview: () => buildEndpoint(`ie-smart-review`, "customer"), // POST
  patchSmartReview: (id: string | number) =>
    buildEndpoint(`ie-smart-review/${id}`, "customer"), // PATCH
};

// UU evidence endpoints (use 'customer' API type)
export const uuEvidenceEndpoints = {
  getCustomerEvidencePeriods: (customerId: string | number) =>
    buildEndpoint(`uc-evidence-period/customer/${customerId}`, "customer"), // GET
  createEvidencePeriod: () => buildEndpoint(`uc-evidence-period`, "customer"), // POST
  updateEvidencePeriod: () => buildEndpoint(`uc-evidence-period`, "customer"), // PUT
  deleteEvidencePeriod: (id: number, customerId: string | number) =>
    buildEndpoint(`uc-evidence-period/${id}/${customerId}`, "customer"), // DELETE
};

// Dependents endpoints (use 'customer' API type)
export const dependentsEndpoints = {
  getDependents: (customerId: string | number) =>
    buildEndpoint(`ie-Customer-Dependent-Dob/${customerId}`, "customer"), // GET
  createDependent: buildEndpoint("ie-Customer-Dependent-Dob", "customer"), // POST
  updateDependent: buildEndpoint("ie-Customer-Dependent-Dob", "customer"), // PUT
  deleteDependent: (id: string | number, customerId: string | number) =>
    buildEndpoint(`ie-Customer-Dependent-Dob/${id}/${customerId}`, "customer"), // DELETE
  patchDependent: (id: string | number) =>
    buildEndpoint(`ie-Customer-Dependent-Dob/${id}`, "customer"), // PATCH
};

export const declarationEndpoints = {
  createDeclaration: () => buildEndpoint(`ie-customer-declaration`, "customer"), // POST
  getDeclaration: (customerId: string | number) =>
    buildEndpoint(`ie-customer-declaration/${customerId}`, "customer"), // GET
  createDeclarationAll: () =>
    buildEndpoint(`ie-customer-declaration-all`, "customer"), // POST
  getDeclarationAll: (customerId: string | number) =>
    buildEndpoint(
      `ie-customer-declaration-all/ie-customer-declaration-all/${customerId}`,
      "customer",
    ), // GET
  fetchDeclarationInfo: () =>
    buildEndpoint(`customer-declarations`, "customer"), // GET
};

export const latestUpdateEndpoints = {
  fetchLatestUpdate: (customerId: string | number) =>
    buildEndpoint(`customer-latest-ie-update-date/${customerId}`, "customer"), // GET
};

// Lender / Agent Pull Consent endpoints (public – no user session required)
// These are called with the invitation token as the Bearer header directly.
export const lenderConsentEndpoints = {
  /** GET /iehub-customer/api/v1/ie-agent-pull-invitation */
  getInvitation: buildEndpoint("ie-agent-pull-invitation", "customer"),
  /** PUT /iehub-customer/api/v1/ie-agent-pull-post-code-verify */
  verifyPostcode: buildEndpoint("ie-agent-pull-post-code-verify", "customer"),
  /** PUT /iehub-customer/api/v1/ie-agent-pull-invitation-status */
  updateStatus: buildEndpoint("ie-agent-pull-invitation-status", "customer"),
};

// /api/v1/ie-customer-declaration

// Endpoint validation utility for new API type system
export const validateEndpoint = (endpoint: string): boolean => {
  if (!endpoint || typeof endpoint !== "string") {
    throw new Error("Endpoint must be a non-empty string");
  }

  // Valid endpoint patterns (relative to baseURL)
  const validPatterns = [
    "/auth/", // Auth endpoints: /auth/*
    "/oauth/", // OAuth endpoints: /oauth/*
    "/users/", // User endpoints: /users/*
    "/forms/", // Form endpoints: /forms/*
    "/files/", // File endpoints: /files/*
    "/system/", // System endpoints: /system/*
    "/monitoring/", // Monitoring endpoints: /monitoring/*
    "/notifications/", // Notification endpoints: /notifications/*
    "/iehub-customer/", // Customer API endpoints: /iehub-customer/*
  ];

  const isValidEndpoint =
    validPatterns.some((pattern) => endpoint.includes(pattern)) ||
    endpoint.startsWith("/iehub-customer/api/v1/");

  if (!isValidEndpoint) {
    throw new Error("Invalid endpoint pattern for security");
  }

  return true;
};

// Dynamic endpoint builder for JSON engine with API type support
export const buildCustomEndpoint = (
  path: string,
  params: Record<string, string> = {},
  apiType: ApiType = "system",
): string => {
  if (!path) {
    throw new Error("Path is required for custom endpoint");
  }

  let endpoint = buildEndpoint(path, apiType);

  // Replace path parameters (e.g. :id -> actual ID)
  Object.entries(params).forEach(([key, value]) => {
    endpoint = endpoint.replace(`:${key}`, encodeURIComponent(value));
  });

  validateEndpoint(endpoint);
  return endpoint;
};

// Utility to determine API type from endpoint path (for debugging)
export const getApiTypeFromEndpoint = (endpoint: string): ApiType | null => {
  if (endpoint.startsWith("/iehub-customer/api/v1/")) {
    return "customer";
  } else if (endpoint.startsWith("/api/auth/")) {
    return "auth";
  } else if (endpoint.startsWith("/api/")) {
    return "system";
  }
  return null;
};

// Utility to get API prefix for a given API type
export const getApiPrefix = (apiType: ApiType): string => {
  if (apiType === "customer") {
    return "/iehub-customer/api/v1";
  } else {
    // Auth and system APIs both use /api prefix
    return "/api";
  }
};

export default {
  auth: authEndpoints,
  oauth: oauthEndpoints,
  user: userEndpoints,
  customer: customerEndpoints,
  terms: termsEndpoints,
  form: formEndpoints,
  file: fileEndpoints,
  system: systemEndpoints,
  monitoring: monitoringEndpoints,
  notification: notificationEndpoints,
  onboarding: onboardingEndpoints,
  budget: budgetEndpoints,
  build: buildCustomEndpoint,
  validate: validateEndpoint,
  getApiType: getApiTypeFromEndpoint,
  getPrefix: getApiPrefix,
};
